import type { PDFDocumentProxy, PDFPageProxy, PageViewport } from 'pdfjs-dist'

function onlyLetters(s: string): string {
  return s.replace(/[^\p{L}\p{N}]/gu, '').toLowerCase()
}

type PageItems = Array<{ str: string; transform: number[]; width: number; height: number }>

async function getPageItems(page: PDFPageProxy): Promise<PageItems> {
  const tc = await page.getTextContent()
  return tc.items.filter((item) => 'str' in item) as PageItems
}

function buildIndex(items: PageItems): { stripped: string[]; itemStarts: number[]; fullStripped: string } {
  const stripped = items.map((item) => onlyLetters(item.str))
  const itemStarts: number[] = []
  let fullStripped = ''
  for (const s of stripped) {
    itemStarts.push(fullStripped.length)
    fullStripped += s
  }
  return { stripped, itemStarts, fullStripped }
}

function drawRects(
  ctx: CanvasRenderingContext2D,
  items: PageItems,
  matchedIndices: Set<number>,
  viewport: PageViewport,
): number | null {
  ctx.fillStyle = 'rgba(255, 210, 0, 0.45)'
  let firstY: number | null = null

  for (const idx of [...matchedIndices].sort((a, b) => a - b)) {
    const item = items[idx]
    const tx = item.transform[4]
    const ty = item.transform[5]
    const w = item.width
    const h = item.height > 0 ? item.height : Math.abs(item.transform[3])
    if (w <= 0 || h <= 0) continue

    const [cx1, cy1, cx2, cy2] = viewport.convertToViewportRectangle([
      tx, ty - h * 0.15, tx + w, ty + h * 0.85,
    ])
    const rx = Math.min(cx1, cx2)
    const ry = Math.min(cy1, cy2)
    const rw = Math.abs(cx2 - cx1)
    const rh = Math.abs(cy2 - cy1)

    ctx.fillRect(rx, ry, rw, rh)
    if (firstY === null) firstY = ry
  }

  return firstY
}

const PROBE_LEN = 30
const START_OFFSETS = [0, 30, 60]
const END_OFFSETS = [0, 30, 60]
const MAX_GAP_RATIO = 3.0
const MIN_GAP_RATIO = 0.3

/**
 * Locate citedText (already stripped to alphanumeric) within a stripped document string.
 * Returns { matchStart, matchEnd } in stripped-string coordinates, or null if not found.
 *
 * Uses dual-probe validation: a start probe and an independent end probe must both
 * match at a plausible span distance apart. Flexible start/end offsets tolerate LLM
 * hallucinated prefixes/suffixes. No back-calculation — highlight starts exactly at
 * the start probe hit, never before it.
 */
export function locateInStripped(
  fullKey: string,
  docStripped: string,
  searchFrom = 0,
): { matchStart: number; matchEnd: number } | null {
  // Short texts: direct match — no dual-probe needed, specific enough on their own
  if (fullKey.length <= PROBE_LEN * 2) {
    const pos = docStripped.indexOf(fullKey, searchFrom)
    return pos === -1 ? null : { matchStart: pos, matchEnd: pos + fullKey.length }
  }

  for (const startOff of START_OFFSETS) {
    if (startOff + PROBE_LEN > fullKey.length) break
    const startKey = fullKey.slice(startOff, startOff + PROBE_LEN)
    let searchPos = searchFrom

    while (true) {
      const startPos = docStripped.indexOf(startKey, searchPos)
      if (startPos === -1) break
      searchPos = startPos + 1

      for (const endOff of END_OFFSETS) {
        const endKeyStart = fullKey.length - PROBE_LEN - endOff
        if (endKeyStart <= startOff + PROBE_LEN) continue  // probes would overlap
        const endKey = fullKey.slice(endKeyStart, endKeyStart + PROBE_LEN)
        const endPos = docStripped.indexOf(endKey, startPos)
        if (endPos === -1) continue

        const matchStart = startPos
        const matchEnd = endPos + PROBE_LEN
        const actualSpan = matchEnd - matchStart
        const expectedSpan = fullKey.length - startOff - endOff

        if (actualSpan < expectedSpan * MIN_GAP_RATIO) continue
        if (actualSpan > expectedSpan * MAX_GAP_RATIO) continue

        return { matchStart, matchEnd }
      }
    }
  }
  return null
}

/** Find all pages (1-based) that contain a valid match for citedText. */
export async function findAllCitationPages(
  pdfDoc: PDFDocumentProxy,
  citedText: string,
): Promise<number[]> {
  const fullKey = onlyLetters(citedText)
  if (fullKey.length < PROBE_LEN * 2) return []
  const hits: number[] = []
  for (let p = 1; p <= pdfDoc.numPages; p++) {
    const page = await pdfDoc.getPage(p)
    const items = await getPageItems(page)
    const { fullStripped } = buildIndex(items)
    if (locateInStripped(fullKey, fullStripped) !== null) hits.push(p)
  }
  return hits
}

/**
 * Highlight citedText on a highlight canvas overlay.
 * Returns firstY (CSS-pixel Y for scroll-to) and crossPage (text continues to next page).
 */
export async function drawHighlightOnCanvas(
  page: PDFPageProxy,
  viewport: PageViewport,
  highlightCanvas: HTMLCanvasElement,
  citedText: string,
): Promise<{ firstY: number | null; crossPage: boolean }> {
  const ctx = highlightCanvas.getContext('2d')!
  ctx.clearRect(0, 0, highlightCanvas.width, highlightCanvas.height)

  const fullKey = onlyLetters(citedText)
  if (fullKey.length < 6) return { firstY: null, crossPage: false }

  const items = await getPageItems(page)
  const { stripped, itemStarts, fullStripped } = buildIndex(items)

  const match = locateInStripped(fullKey, fullStripped)
  if (!match) return { firstY: null, crossPage: false }

  const { matchStart: matchPos, matchEnd } = match
  const crossPage = matchPos + fullKey.length > fullStripped.length

  const matchedIndices = new Set<number>()
  for (let i = 0; i < items.length; i++) {
    const iStart = itemStarts[i]
    const iEnd = iStart + stripped[i].length
    if (matchPos < iEnd && matchEnd > iStart) matchedIndices.add(i)
  }
  if (matchedIndices.size === 0) return { firstY: null, crossPage }

  const firstY = drawRects(ctx, items, matchedIndices, viewport)
  return { firstY, crossPage }
}

/**
 * Highlight the continuation of a cross-page citation on the next page.
 * Searches for the tail PROBE_LEN chars of citedText at the start of this page.
 */
export async function drawHighlightContinuation(
  page: PDFPageProxy,
  viewport: PageViewport,
  highlightCanvas: HTMLCanvasElement,
  citedText: string,
): Promise<void> {
  const fullKey = onlyLetters(citedText)
  const tailKey = fullKey.slice(-Math.min(PROBE_LEN, fullKey.length))
  if (tailKey.length < 6) return

  const ctx = highlightCanvas.getContext('2d')!
  const items = await getPageItems(page)
  const { stripped, itemStarts, fullStripped } = buildIndex(items)

  const tailPos = fullStripped.indexOf(tailKey)
  if (tailPos === -1) return

  const matchEnd = tailPos + tailKey.length
  const matchedIndices = new Set<number>()
  for (let i = 0; i < items.length; i++) {
    const iStart = itemStarts[i]
    const iEnd = iStart + stripped[i].length
    if (matchEnd > iStart && iEnd > 0) matchedIndices.add(i)
  }

  drawRects(ctx, items, matchedIndices, viewport)
}

/** Clear all highlights from a highlight canvas. */
export function clearHighlightCanvas(highlightCanvas: HTMLCanvasElement): void {
  const ctx = highlightCanvas.getContext('2d')!
  ctx.clearRect(0, 0, highlightCanvas.width, highlightCanvas.height)
}
